import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
}

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Always force light theme
  const [theme] = useState<Theme>("light");

  useEffect(() => {
    // Clear any existing dark theme preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      localStorage.removeItem("theme");
    }

    // Ensure dark class is removed from document
    document.documentElement.classList.remove("dark");

    // Set light theme in localStorage
    localStorage.setItem("theme", "light");
  }, []);

  // No-op function since we're always light mode
  const toggleTheme = () => {
    // Theme toggling is disabled - always light mode
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
